import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { bindActionCreators, compose } from 'redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withNamespaces } from 'react-i18next';
import { startCase } from 'lodash';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { logout, updateLocalUserData } from '../../redux/actions/AuthActions';
import { showError, showSuccess } from '../../redux/actions/GlobalToastActions';
import { fetchMerchantDetails } from '../../redux/actions/MerchantsActions';
import { fetchFeatureFlags, fetchMerchantFeatureFlags } from '../../redux-toolkit/featureFlagsSlice';
import { Routes as configRouter } from '../../config/Routes';
import { getMerchantDetails } from '../../redux/reducers/MerchantsReducer';
import AccountsService from '../../services/AccountsService';
import { checkAllowedCapabilities } from '../../utils/Capability';
import { companyName, AuthKeys } from '../../utils/Constants';
import NavbarItem from './NavBarItem';
import NavBarSubmenu from './NavBarSubmenu';
import AccountBadge from '../common/AccountBadge';
import * as GlobalModalActions from '../../redux/actions/GlobalModalActions';
import ModalTypes from '../Modal/ModalTypes';
import EditProfileModal from '../Users/EditProfileModal';
import LocalStorageHelper from '../../utils/LocalStorageHelper';
import { getUserData, idUser, isInternalUser, isTransferEnabled, referralEnabled } from '../../redux/reducers/AuthReducer';
import AppEnv from '../Simple/AppEnv';
import { buildPathWithParams } from '../../utils/Paths';
import config from '../../envConfig/config';
import { usePanelType } from '../../hooks/usePanelType';
import getNavbarConfig from '../../config/NavBarConfig';

const NavBar = (props) => {
  const { fetchMerchantDetails, updateLocalUserData, showError, idUser, t, isInternalUser, userData, match, isTransferEnabled, referralEnabled, logout, showModal, fetchFeatureFlags, fetchMerchantFeatureFlags } = props;

  const [scrollTop, setScrollTop] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [errorAvatar, setErrorAvatar] = useState(false);
  const navbarContentElement = useRef(null);

  useEffect(() => {
    const navbarState = LocalStorageHelper.getData('navbarState');
    const initialCollapsed = navbarState ? navbarState.collapsed : false;
    setCollapsed(initialCollapsed);

    window.addEventListener('resize', onResize);
    onResize();
    navbarContentElement.current.addEventListener('scroll', handleScroll);

    (async () => {
      try {
        if (!isInternalUser) fetchMerchantDetails();
        const profileDetails = await AccountsService.fetchAccountDetails(idUser);
        updateLocalUserData(profileDetails);
        
        // Load global feature flags
        fetchFeatureFlags();
        
        // If not an internal user, also load merchant-specific feature flags
        if (!isInternalUser && profileDetails && profileDetails.idMerchant) {
          fetchMerchantFeatureFlags(profileDetails.idMerchant);
        }
      } catch (err) {
        showError(t('errorFetchProfile'));
      }
    })();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [fetchMerchantDetails, updateLocalUserData, showError, idUser, t, isInternalUser, fetchFeatureFlags, fetchMerchantFeatureFlags]);

  const onResize = () => {
    const { innerWidth } = window;
    if (innerWidth < 993 && !collapsed) {
      setCollapsed(!collapsed);
      LocalStorageHelper.storeData('navbarState', { collapsed: !collapsed });
    }
  };

  const toggleCollapsed = () => {
    LocalStorageHelper.storeData('navbarState', { collapsed: !collapsed });
    setCollapsed(!collapsed);
    window.dispatchEvent(new Event('resize'));
  };

  const handleScroll = () => {
    setScrollTop(navbarContentElement.current.scrollTop);
  };

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
  };

  const handleUserClick = (e) => {
    e.preventDefault();
    showModal(
      {
        children: <EditProfileModal />,
      },
      ModalTypes.CLEAN_MODAL
    );
  };

  const renderNavbarItem = (navbarItem, index) => {
    const { route, icon, text, items = [], capabilities = {}, defaultPath = false, badge, hide } = navbarItem;
    const userMerchant = LocalStorageHelper.getData(AuthKeys.USER_DATA);
    const notMerchantTest = userMerchant && userMerchant.idMerchant && !referralEnabled;

    if (
      (!isInternalUser && route === configRouter.REFERRED_MERCHANTS && (notMerchantTest || notMerchantTest === undefined)) ||
      !checkAllowedCapabilities(capabilities) ||
      (route === configRouter.TRANSFER && !isTransferEnabled) ||
      hide
    ) {
      return null;
    }

    const newFeatureMessage = route === configRouter.KYC && userData.firstTimeKyc;
    const active = items.length ? items.some((item) => item.route === match.path) : match.path === route;
    const menuRoute = defaultPath ? buildPathWithParams(route, defaultPath) : route;
    const uniqueKey = `${route}-${text}-${index}`;

    if (!items.length) {
      return <NavbarItem route={menuRoute} icon={icon} text={t(`nav${text}`)} active={active} key={uniqueKey} badge={badge} newFeatureMessage={newFeatureMessage} />;
    }

    const allowedItems = items.filter(({ capabilities = {} }) => checkAllowedCapabilities(capabilities));
    if (!allowedItems.length) return null;

    return <NavBarSubmenu navbarScrollTop={scrollTop} route={route} icon={icon} text={t(`nav${text}`)} active={active} key={uniqueKey} items={allowedItems} path={match.path} />;
  };

  const navbarClasses = classNames('default-navbar', { collapsed });
  const hrefHome = '#';
  const logoUrls = config.REACT_APP_LOGO_URL;
  const panelType = usePanelType();
  const navbarConfig = getNavbarConfig(panelType);

  return (
    <nav className={navbarClasses} role="navigation">
      <div className="default-navbar-header" onClick={toggleCollapsed}>
        <a className="default-navbar-brand" href={hrefHome} onClick={(e) => e.preventDefault()}>
          <div className="navbar-logo-small">
            <FontAwesomeIcon icon={faBars} />
          </div>
          <div className="navbar-logo">
            <AppEnv />
            <img src={`${logoUrls.defaultLogo}`} alt={companyName} />
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
        </a>
      </div>
      <Scrollbars autoHide className="default-navbar-content">
        <div ref={navbarContentElement}>
          <ul>
            {navbarConfig.map((navBarItem, index) => renderNavbarItem(navBarItem, index))}
            <NavbarItem route={configRouter.LOGIN} icon="sign-out" text={t('labelLogout')} active={false} handleClick={handleLogout} />
          </ul>
        </div>
      </Scrollbars>
      <div className="default-navbar-bottom" onClick={handleUserClick}>
        <div className="profile">
          <div className="user-info">
            {userData.photo && !errorAvatar ? (
              <img src={userData.photo} alt="Profile" className="user-info" onError={() => setErrorAvatar(true)} onLoad={() => setErrorAvatar(false)} />
            ) : (
              <AccountBadge letter={userData.name && userData.name[0]} classes="bg-purple avatar-badge" />
            )}
          </div>
        </div>
        <div className="profile-text">{startCase(userData.name)}</div>
        <FontAwesomeIcon icon={faChevronRight} />
      </div>
    </nav>
  );
};

const mapStateToProps = (state) => {
  return {
    idUser: idUser(state),
    userData: getUserData(state),
    isInternalUser: isInternalUser(state),
    merchantDetails: getMerchantDetails(state),
    isTransferEnabled: isTransferEnabled(state),
    referralEnabled: referralEnabled(state),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      logout,
      ...GlobalModalActions,
      fetchMerchantDetails,
      updateLocalUserData,
      showSuccess,
      showError,
      fetchFeatureFlags,
      fetchMerchantFeatureFlags,
    },
    dispatch
  );

export default compose(withNamespaces(), connect(mapStateToProps, mapDispatchToProps))(NavBar);
